/**
 * Audit Logs Page - Company Admin
 */
import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import { ScrollText, Search, Filter, ChevronDown, ChevronRight, User, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import * as api from '@/lib/companyAdminApi';

const ACTION_COLORS = {
  CREATE: 'bg-green-100 text-green-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
  LOGIN: 'bg-purple-100 text-purple-700',
};

const getActionColor = (action) => {
  if (action.includes('CREATE')) return ACTION_COLORS.CREATE;
  if (action.includes('UPDATE') || action.includes('SET')) return ACTION_COLORS.UPDATE;
  if (action.includes('DELETE') || action.includes('REVOKE')) return ACTION_COLORS.DELETE;
  if (action.includes('LOGIN')) return ACTION_COLORS.LOGIN;
  return 'bg-gray-100 text-gray-700';
};

export default function AuditLogsPage() {
  const { token } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchLogs = useLatestCallback(async () => {
    try {
      const params = {
        limit: 50,
        offset: page * 50,
      };
      if (actionFilter !== 'all') params.action = actionFilter;
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      
      const response = await api.getAuditLogs(params);
      const items = response.data.items || [];
      setLogs(page === 0 ? items : [...logs, ...items]);
      setHasMore(items.length === 50);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, entityFilter, page, fetchLogs]);

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredLogs = logs.filter(log =>
    log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.entity_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.entity_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatJson = (obj) => {
    if (!obj) return 'null';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-gray-500">Track all administrative actions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="RESTAURANT_CREATE">Restaurant Create</SelectItem>
            <SelectItem value="RESTAURANT_UPDATE">Restaurant Update</SelectItem>
            <SelectItem value="RESTAURANT_DELETE">Restaurant Delete</SelectItem>
            <SelectItem value="USER_CREATE">User Create</SelectItem>
            <SelectItem value="USER_UPDATE">User Update</SelectItem>
            <SelectItem value="USER_DELETE">User Delete</SelectItem>
            <SelectItem value="SETTING_CREATE">Setting Create</SelectItem>
            <SelectItem value="SETTING_UPDATE">Setting Update</SelectItem>
            <SelectItem value="FLAG_CREATE">Flag Create</SelectItem>
            <SelectItem value="FLAG_DEFAULT_SET">Flag Default Set</SelectItem>
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Entity Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="restaurant">Restaurant</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="platform_setting">Setting</SelectItem>
            <SelectItem value="feature_flag">Feature Flag</SelectItem>
            <SelectItem value="announcement">Announcement</SelectItem>
            <SelectItem value="ticket">Ticket</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Total Logs</span>
              <ScrollText className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-2xl font-bold mt-2">{logs.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Creates</span>
              <Badge className={ACTION_COLORS.CREATE}>CREATE</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">
              {logs.filter(l => l.action?.includes('CREATE')).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Updates</span>
              <Badge className={ACTION_COLORS.UPDATE}>UPDATE</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">
              {logs.filter(l => l.action?.includes('UPDATE') || l.action?.includes('SET')).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Deletes</span>
              <Badge className={ACTION_COLORS.DELETE}>DELETE</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">
              {logs.filter(l => l.action?.includes('DELETE') || l.action?.includes('REVOKE')).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <Collapsible key={log.id} open={expandedRows.has(log.id)} onOpenChange={() => toggleRow(log.id)} asChild>
                    <>
                      <TableRow className="cursor-pointer hover:bg-gray-50">
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                              {expandedRows.has(log.id) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                        <TableCell>
                          <Badge className={getActionColor(log.action)}>{log.action}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="font-medium">{log.entity_type}</span>
                            {log.entity_id && (
                              <span className="text-gray-500 ml-1">#{log.entity_id}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-gray-400" />
                            <span>{log.actor_type}</span>
                            {log.actor_id && <span className="text-gray-500">#{log.actor_id}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(log.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {log.ip || '-'}
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={6} className="py-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Before</p>
                                <pre className="p-3 bg-white border rounded text-xs overflow-auto max-h-40">
                                  {formatJson(log.before_json)}
                                </pre>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">After</p>
                                <pre className="p-3 bg-white border rounded text-xs overflow-auto max-h-40">
                                  {formatJson(log.after_json)}
                                </pre>
                              </div>
                            </div>
                            {log.meta_json && (
                              <div className="mt-4">
                                <p className="text-xs font-medium text-gray-500 mb-1">Metadata</p>
                                <pre className="p-3 bg-white border rounded text-xs overflow-auto max-h-20">
                                  {formatJson(log.meta_json)}
                                </pre>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Load More */}
      {hasMore && !loading && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => setPage(p => p + 1)}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}
